import { Check, Copy } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface Props {
  body: string
}

export default function CopyTextButton({ body }: Props) {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)

  const canCopyText =
    typeof navigator !== "undefined" &&
    typeof navigator.clipboard?.writeText === "function"
  const hasBody = body.trim().length > 0

  if (!hasBody || !canCopyText) {
    return null
  }

  async function handleCopyText() {
    setCopyError(null)
    try {
      await navigator.clipboard.writeText(body)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopyError("Couldn't copy — please copy the text manually.")
    }
  }

  const label = copyError ?? (copied ? "Copied" : "Copy text")

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          onClick={() => void handleCopyText()}
          className="bg-background/80 text-foreground hover:bg-background absolute top-2 right-2 rounded-md shadow-sm"
        >
          {copied ? (
            <Check className="size-3.5" aria-hidden />
          ) : (
            <Copy className="size-3.5" aria-hidden />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  )
}
